// =============================================================================
// ui/tabs/CustomTagEditor.tsx — Settings → Preferences → custom tags.
// -----------------------------------------------------------------------------
// 🔴 THE GAP THIS CLOSES: tags were visible everywhere and creatable nowhere.
//
// A trade's chips render from this registry, the filter drawer builds its option
// list from it, and the trade form can create one inline — but there was no
// screen in the app that could rename, recolour or remove one, and a user who
// had never opened a trade had no way to make a first tag at all. So the effect
// was on screen with no cause, which reads as a feature that half-works.
//
// ⚠ SEPARATE FROM MISTAKE TAGS, and the two editors sit next to each other, so
// the difference has to be legible on screen: mistake tags are a vocabulary that
// feeds the controllable-loss classifier; these are neutral labels that feed
// nothing but the filter.
// =============================================================================

import { useState } from 'react';
import { usePreferences } from '../state/preferences';
import { parseTagRegistry, type UserTag } from '../../selectors/accounts';
import {
  TAG_NAME_MAX, addUserTag, editUserTag, removeUserTag, toStoredTags,
} from '../../selectors/userTags';

export function CustomTagEditor() {
  const { prefs, update } = usePreferences();
  const registry = parseTagRegistry(prefs?.customTags);

  const [draft, setDraft] = useState('');
  const [note, setNote] = useState<string | null>(null);
  // ⚠ TWO-STEP DELETE, which is this client's pattern for a destructive control
  // and is doing more work here than usual: there is no undo, and the label
  // vanishes from every trade that carried it.
  const [confirming, setConfirming] = useState<string | null>(null);

  const commit = (next: readonly UserTag[]): void => {
    // ⚠ Identity check, not a deep compare. Every mutator returns the SAME array
    // when it changed nothing, so this skips a write that would only bump the
    // row's timestamp — and it is what makes a rejected rename silent on disk.
    if (next === registry) return;
    void update({ customTags: toStoredTags(next) });
  };

  const add = (): void => {
    const name = draft.trim();
    if (!name) return;
    // ⚠ The id is generated HERE, not in the domain, so the rule stays pure and
    // testable. `crypto.randomUUID` is available in every browser this app
    // supports and in jsdom.
    const next = addUserTag(registry, name, crypto.randomUUID());
    if (next === registry) { setNote(`You already have a tag called “${name}”.`); return; }
    setNote(null);
    setDraft('');
    commit(next);
  };

  const rename = (id: string, name: string): void => {
    const next = editUserTag(registry, id, { name });
    if (next === registry && name.trim()) {
      setNote(`Another tag is already called “${name.trim()}”.`);
      return;
    }
    setNote(null);
    commit(next);
  };

  return (
    <section className="card" data-testid="custom-tags">
      <h3>Custom tags</h3>
      <p className="muted">
        Neutral labels for organising trades — separate from mistake tags, and
        they do not affect the controllable-loss classifier. Create them here or
        inline on any trade’s Tags field. Renaming or recolouring one updates it
        everywhere it is used.
      </p>

      {registry.length === 0 ? (
        <p className="muted">
          No custom tags yet — add one below, or create tags inline on any trade.
        </p>
      ) : (
        <div className="utag-rows">
          {registry.map((tag) => (
            <div className="utag-row" key={tag.id}>
              <input
                type="color" className="utag-color" value={tag.color || '#64748b'}
                aria-label={`Colour for ${tag.name}`}
                onChange={(e) => commit(editUserTag(registry, tag.id, { color: e.target.value }))}
              />
              {/* ⚠ COMMITTED ON BLUR, not per keystroke — the same reason the
                  mistake-tag box next to this one is: a write per character
                  saves half a word as the tag's real name, and this one is
                  rendered on every trade that carries it. */}
              <input
                type="text" className="utag-name" defaultValue={tag.name}
                maxLength={TAG_NAME_MAX} aria-label={`Name for ${tag.name}`}
                onBlur={(e) => rename(tag.id, e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
              />
              {confirming === tag.id ? (
                <>
                  <button
                    type="button" className="btn danger btn-sm"
                    onClick={() => { setConfirming(null); commit(removeUserTag(registry, tag.id)); }}
                  >
                    Delete “{tag.name}”
                  </button>
                  <button type="button" className="btn btn-sm" onClick={() => setConfirming(null)}>
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  type="button" className="btn btn-sm"
                  onClick={() => setConfirming(tag.id)}
                >
                  Delete
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="utag-add">
        <input
          type="text" placeholder="New tag name" maxLength={TAG_NAME_MAX}
          aria-label="New tag name"
          value={draft}
          onChange={(e) => { setDraft(e.target.value); setNote(null); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
        />
        <button type="button" className="btn" onClick={add} disabled={!draft.trim()}>
          + Add tag
        </button>
      </div>

      {note ? <p className="muted utag-note" role="status">{note}</p> : null}

      {/* ⚠ SAID OUT LOUD, because it is not recoverable from this screen and it
          is NOT what the old client does. There, deleting a tag rewrites every
          trade that carried it; here the trades are untouched and the label
          simply stops being known. Same thing to look at, very different thing
          to go wrong — and a trader deserves to know which one they are getting
          before they press a button with no undo. */}
      <p className="muted utag-note">
        Deleting a tag removes the label from every trade that carries it. The
        trades themselves are not changed, and nothing else is lost — but the tag
        cannot be brought back, and re-creating it will not re-label anything.
      </p>
    </section>
  );
}
